// @ts-strict-ignore
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ValidatorFactory } from '@shared/validator.factory';
import { DrillingHoleType } from '@3d-scene/model3d/util/drillinghole/drilling-hole-type';
import { Dictionary } from 'typescript-collections';
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProductionCountryVO } from '@article/model/vo/production-country.vo';
import { DrillingHoleCountryManager } from '../../../components/article/manager/drilling-hole-country.manager';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { ArticleManager } from '@article/manager/article.manager';
import { UserModel } from '@user/model/user.model';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ProjectModel } from '../../../model/project.model';
import { EditDrillingHoleMode } from '../edit-drilling-hole.mode';
import { RangeOnLineConnectionUtils } from '@3d-scene/model3d/util/range-on-line-connection-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { DrillingHoleRange, DrillingHoleSize } from '@3d-scene/model3d/util/drillinghole/drilling-hole-size';
import { LocaleUtils } from '@shared/util/locale-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { DrillingholePresetVO } from '../drilling-hole.vo';
import { StringUtils } from '@shared/util/string-utils';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CustomValidators } from '@shared/custom-validators';
import { VolatileDataSetHolder } from '../../../util/volatile-data-set.holder';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageService } from '@article/service/article-usage.service';

export interface AccessoryList {
  items: ArticleVO[];
  selectedItem: ArticleVO;
}

@Component({
  standalone: false,
  templateUrl: `./drilling-hole-wizard.component.html`,
  selector: 'app-drilling-hole-wizard',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrillingHoleWizardComponent implements OnInit, AfterViewInit {

  @Input({required: true}) public properties: {
    currentMode: EditDrillingHoleMode,
    totalLength: number,
    drillingHoleArticles: ArticleVO[],
    carrierHasPreset: boolean,
    presetStartOffset: number,
    presetDrillingholeDistance: number,
    drillingHoleInfo?: DrillingHoleInfo,
  };

  @Output() public finish = new EventEmitter<DrillingHoleInfo | number | DrillingHoleInfo[] | null>();

  public EditDrillingHoleMode = EditDrillingHoleMode;
  public DrillingHoleType = DrillingHoleType;
  private _selectedType: string;

  public popupLabel: string;
  public totalRailLengthLabel: string;
  public currentMode: EditDrillingHoleMode;

  // total length of drilling hole connection in mm. This is different from length of rail, because drilling hole connection is extended.
  private _totalLength: number = 0;

  // free space in mm
  public freeSpace: number = 0;

  public shapeTypeMap: Dictionary<string, DrillingHoleSize[]> = new Dictionary<string, DrillingHoleSize[]>();

  private _drillingHoleInfo: DrillingHoleInfo;
  private _originalDrillingHoleInfo: DrillingHoleInfo = null;
  private _drillingHoleArticles: any[];
  private _selectedDrillingHoleArticle: ArticleVO;
  private _carrierHasPreset: boolean;
  private _presetStartOffset: number = 0;
  private _presetDrillingholeDistance: number;
  private _selectedAccessories: ArticleVO[];
  private _preselectedAccessoryIds: any[] = [];

  private _selectedProductionCountry: ProductionCountryVO;
  private _userDefaultProductionCountry: ProductionCountryVO;

  private _acceptingConnections: number = 0;

  public accessories: ArticleVO[] = [];

  private articleIds: any[] = [];
  protected _articleToAdd: ArticleVO = null;

  private currentSelectedCountry: ProductionCountryVO;
  public productionCountries: ProductionCountryVO[] = [];

  public accessoryLists: AccessoryList[] = [];
  public accessoryDropDownListEnabled: boolean = true;

  public shapeTypes: DrillingHoleType[] = [];

  private _selectedSize: DrillingHoleSize = null;

  public holeCountInputDisabled: boolean = false;
  public endOffsetInputDisabled: boolean = false;

  public form: FormGroup;

  public get selectedType(): string {
    return this._selectedType;
  }

  public set selectedType(value: string) {
    this._selectedType = value;
    this.shapeTypeGroupValueCommitHandler();
  }

  public get totalLength(): number {
    return this._totalLength;
  }

  public set totalLength(value: number) {
    this._totalLength = value;
    this.updateFreeSpace();
  }

  private get acceptingConnections(): number {
    return this._acceptingConnections;
  }

  private set acceptingConnections(value: number) {
    this._acceptingConnections = value;
    this.createAccessoryLists();
    this.cdRef.markForCheck();
  }

  public get selectedSize(): DrillingHoleSize {
    return this._selectedSize;
  }

  public set selectedSize(size: DrillingHoleSize) {
    this._selectedSize = size;
    this.sizeDropDownListChangeHandler();
    this.sizeDropDownListValueCommitHandler();
    this.startOffsetInputHandler();
    this.cdRef.markForCheck();
  }

  public get selectedProductionCountry(): ProductionCountryVO {
    return this.currentSelectedCountry;
  }

  public set selectedProductionCountry(country: ProductionCountryVO) {
    this.currentSelectedCountry = country;
    if (!country || country === this._selectedProductionCountry) {
      return;
    }
    this.shapeTypeMap = new Dictionary<string, DrillingHoleSize[]>();
    this.shapeTypes = [];
    this._selectedSize = null;
    this._selectedProductionCountry = country;
    if (this._drillingHoleInfo) {
      this._drillingHoleInfo.shapeType = null;
    }
    this.prepareDrillingHoleData();
    this.setDrillingHoleInfo();
    this.updateAcceptingConnections();
    this.toggleDrillingHoleInputComponents(false);
    this.cdRef.markForCheck();
  }

  public set drillingHoleInfo(info: DrillingHoleInfo) {
    if (info) {
      this._drillingHoleInfo = info;
    }
  }

  public get drillingHoleInfo(): DrillingHoleInfo {
    return this._drillingHoleInfo;
  }

  public get carrierHasPreset(): boolean {
    return this._carrierHasPreset;
  }

  public set carrierHasPreset(value: boolean) {
    this._carrierHasPreset = value;
  }

  public get presetStartOffset(): number {
    return this._presetStartOffset;
  }

  public set presetStartOffset(value: number) {
    if (NumberUtils.isNumber(value)) {
      this._presetStartOffset = value;
    } else {
      this._presetStartOffset = 0;
    }
  }

  public get presetDrillingholeDistance(): number {
    return this._presetDrillingholeDistance;
  }

  public set presetDrillingholeDistance(value: number) {
    this._presetDrillingholeDistance = value;
  }

  public set drillingHoleArticles(value: any[]) {
    this._drillingHoleArticles = value;
  }

  public get drillingHoleArticles(): any[] {
    return this._drillingHoleArticles;
  }

  private _clickHandler = (event) => event.stopPropagation();

  constructor(
    private _bsModalRef: BsModalRef,
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private elRef: ElementRef,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private drillingHoleCountryManager: DrillingHoleCountryManager,
    private articleAccessoryManager: ArticleAccessoryManager,
    private articleManager: ArticleManager,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private articleUsageService: ArticleUsageService,
    private validatorFactory: ValidatorFactory,
  ) {
    this.form = this.formBuilder.group({
      startOffset: ['0'],
      holeCount: ['1'],
      endOffset: ['0'],
    });
    this.form.controls.startOffset.valueChanges.subscribe(() => this.updateFreeSpace());
    this.form.controls.holeCount.valueChanges.subscribe(() => {
      this.endOffsetInputDisabled = !(Number(this.form.controls.holeCount.value) > 1 && !this.carrierHasPreset);
    });
    this.form.controls.endOffset.valueChanges.subscribe(() => this.updateFreeSpace());
  }

  public ngOnInit(): void {
    if (this.properties) {
      this.currentMode = this.properties.currentMode;
      this.carrierHasPreset = this.properties.carrierHasPreset;
      this.totalLength = this.properties.totalLength;
      this.drillingHoleArticles = this.properties.drillingHoleArticles;
      this.presetStartOffset = this.properties.presetStartOffset;
      this.presetDrillingholeDistance = this.properties.presetDrillingholeDistance;
      this._originalDrillingHoleInfo = this.properties.drillingHoleInfo;
      if (this._originalDrillingHoleInfo) {
        this._drillingHoleInfo = this._originalDrillingHoleInfo.clone();
      }
      this.prepareProductionCountryData();
      this.prepareDrillingHoleData();
      this.updateAcceptingConnections();
      this.toggleDrillingHoleInputComponents(false);
    }
    this.popupLabel = this.getPopupTitle();
    this.totalRailLengthLabel = this.getRailTotalLengthLabel();
    this.setValidators();
    this.cdRef.markForCheck();
  }

  public ngAfterViewInit(): void {
    this.elRef.nativeElement.addEventListener('click', this._clickHandler);
    this.cdRef.markForCheck();
  }

  public close(data: DrillingHoleInfo | number | DrillingHoleInfo[] | null = null): void {
    this.elRef.nativeElement.removeEventListener('click', this._clickHandler);
    this._bsModalRef.hide();
    this.finish.emit(data);
  }

  public save(): void {
    if (this.form.invalid) {
      return;
    }
    this.updateFreeSpace();
    if (this.freeSpace <= 0) {
      this.alertHelper.showError('editDrillingHolePopup.title.invalid_input', 'editDrillingHolePopup.message.invalid_input');
      return;
    }
    if (this.currentMode === EditDrillingHoleMode.DRILLING_HOLE_WIZARD_MODE) {
      this.saveWizardDrillingHoles();
    } else {
      this.saveSingleDrillingHole();
    }
  }

  private setValidators(): void {
    if (this.form) {
      if (this.currentMode === EditDrillingHoleMode.DRILLING_HOLE_WIZARD_MODE) {
        let startMin: number = null;
        let startMax: number = null;
        if (this.carrierHasPreset) {
          const startDrillingHoleRange: DrillingHoleRange = this.getStartDrillingHoleRange();
          if (startDrillingHoleRange) {
            startMin = startDrillingHoleRange.min;
            startMax = startDrillingHoleRange.max;
          }
        }
        this.form.controls.startOffset.setValidators(
          Validators.compose([
            CustomValidators.trimRequired,
            this.validatorFactory.number({min: startMin, max: startMax}),
          ])
        );
        this.form.controls.holeCount.setValidators(
          Validators.compose([
            CustomValidators.trimRequired,
            this.validatorFactory.number({min: 1, max: 100}),
          ])
        );
        this.form.controls.endOffset.setValidators(
          Validators.compose([
            CustomValidators.trimRequired,
            this.validatorFactory.number(),
          ])
        );
      } else {
        this.form.controls.startOffset.setValidators(
          Validators.compose([
            CustomValidators.trimRequired,
            this.validatorFactory.number(),
          ])
        );
      }
    }
  }

  private prepareProductionCountryData(): void {
    // determine default production country for the user independently from passed drilling-holes
    // it will be used as fallback for drilling-holes production-countries for which are not specified
    const defaultCountry: ProductionCountryVO = this.drillingHoleCountryManager.findUserDefaultProductionCountry(
      this.userModel.user,
      this.drillingHoleCountryManager.productionCountries
    );
    let preselectionCountry: ProductionCountryVO;
    const productionCountriesForArticles: any[] = this.drillingHoleCountryManager.getProductionCountriesForArticles(
      this._drillingHoleArticles,
      defaultCountry
    );
    if (this._drillingHoleInfo) {
      // in case dialog opened to edit existing drilling-hole - preselection country should be determined
      // only by countries in which given drilling-hole is available
      const drillingHoleArticle: ArticleVO = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
      preselectionCountry = this.drillingHoleCountryManager.findUserDefaultProductionCountryByArticle(
        this.userModel.user,
        drillingHoleArticle,
        defaultCountry
      );
    } else if (this._carrierHasPreset) {
      // in case dialog opened for preset - preselection country should be determined by only drilling-hole linked to this preset
      const presetArticle: ArticleVO = this.getPresetArticle();
      const presetProductionCountries: any[] = this.drillingHoleCountryManager.getProductionCountriesForArticle(presetArticle, defaultCountry);
      preselectionCountry = this.drillingHoleCountryManager.findUserDefaultProductionCountry(this.userModel.user, presetProductionCountries);
      // add countries taken from preset to list of countries need to be shown in the dialog
      ArrayUtils.mergeArrays(productionCountriesForArticles, presetProductionCountries);
    } else {
      // otherwise determine preselection by countries in which the given drilling-holes are available
      preselectionCountry = this.drillingHoleCountryManager.findUserDefaultProductionCountry(this.userModel.user, productionCountriesForArticles);
    }

    this._userDefaultProductionCountry = defaultCountry;
    this._selectedProductionCountry = preselectionCountry;
    this.productionCountries = this.drillingHoleCountryManager.getSortedProductionCountryCollection(productionCountriesForArticles);
    this.currentSelectedCountry = this._selectedProductionCountry;
  }

  private updateAcceptingConnections(): void {
    if (this.drillingHoleInfo && this.drillingHoleInfo.usageId) {
      const usage: ArticleUsageVO = this.projectModel.getArticleUsage(this.drillingHoleInfo.usageId);
      if (usage) {
        const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(usage);
        if (article) {
          this.acceptingConnections = this.getAcceptingConnectionsCount(article);
        }
      }
      this.setAccessories(this.drillingHoleInfo);
    } else {
      this.accessoryLists = [{ items: [], selectedItem: null }];
    }
  }

  private getPopupTitle(): string {
    switch (this.currentMode) {
      case EditDrillingHoleMode.NEW_DRILLING_HOLE_MODE:
        return 'editDrillingHolePopup.title.new';
      case EditDrillingHoleMode.EDIT_DRILLING_HOLE_MODE:
        return 'editDrillingHolePopup.title.edit';
      case EditDrillingHoleMode.DRILLING_HOLE_WIZARD_MODE:
        return 'editDrillingHolePopup.title.wizard';
      default:
        return '';
    }
  }

  private getTotalLengthMM(): number {
    if (NumberUtils.isNotNumber(this.totalLength)) {
      return 0;
    }
    return RangeOnLineConnectionUtils.getOriginalTotalLengthInMM(this.totalLength);
  }

  private updateFreeSpace(): void {
    const startOffset: number = this.localeResourceManager.parseNumber(this.form.controls.startOffset.value);
    const endOffset: number = this.localeResourceManager.parseNumber(this.form.controls.endOffset.value);
    if (NumberUtils.isNotNumber(startOffset) || NumberUtils.isNotNumber(endOffset) || NumberUtils.isNotNumber(this.getTotalLengthMM())) {
      return;
    }

    if (this.currentMode === EditDrillingHoleMode.DRILLING_HOLE_WIZARD_MODE) {
      this.freeSpace = this.getTotalLengthMM() - startOffset - endOffset;
    } else {
      if (this.getTotalLengthMM() > startOffset) {
        this.freeSpace = this.getTotalLengthMM() - startOffset;
      } else {
        this.freeSpace = startOffset;
      }
    }
  }

  private saveSingleDrillingHole(): void {
    if (!this._drillingHoleInfo) {
      return;
    }
    this._drillingHoleInfo.dirty = false;
    this._drillingHoleInfo.accessoryArticles = [];
    if (ArrayUtils.isNotEmpty(this._selectedAccessories)) {
      let articleCount: number = 0;
      let i: number = 0;
      let article: ArticleVO = null;
      while (i < this._selectedAccessories.length) {
        article = this._selectedAccessories[i];
        if (article) {
          articleCount++;
          this._drillingHoleInfo.accessoryArticles.push(article);
          if (this._preselectedAccessoryIds.indexOf(article.identity) < 0) {
            this._drillingHoleInfo.dirty = true;
          }
        }
        i++;
      }
      if (articleCount < this._preselectedAccessoryIds.length) {
        this._drillingHoleInfo.dirty = true;
      }
    } else if (
      ArrayUtils.isNotEmpty(this._preselectedAccessoryIds) &&
      this._selectedAccessories &&
      this._selectedAccessories.length < this._preselectedAccessoryIds.length
    ) {
      this._drillingHoleInfo.dirty = true;
    }
    if (this.currentMode === EditDrillingHoleMode.EDIT_DRILLING_HOLE_MODE && this.carrierHasPreset) {
      this.saveChangesToOriginal();
      this.close(this._drillingHoleInfo);
    } else {
      const drillingHoleArticle: ArticleVO = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
      const drillingHoleSizePosOnRailOffset: number = DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(drillingHoleArticle);

      if (NumberUtils.isNotNumber(drillingHoleSizePosOnRailOffset)) {
        return;
      }

      const startOffset: number = this.localeResourceManager.parseNumber(this.form.controls.startOffset.value);
      if (startOffset < -drillingHoleSizePosOnRailOffset) {
        this.form.controls.startOffset.setValue(this.localeResourceManager.formatNumber(-drillingHoleSizePosOnRailOffset, false));
        this.alertHelper.showError('editDrillingHolePopup.message.offset_too_low', 'editDrillingHolePopup.message.offset_too_low');
        return;
      }
      this._drillingHoleInfo.position = UnitConverter.fromTo('mm', '3d', startOffset);
      if (!this._originalDrillingHoleInfo || !this._originalDrillingHoleInfo.equals(this.drillingHoleInfo)) {
        this._drillingHoleInfo.dirty = true;
      }
      this.projectModel.projectChangeHandler();
      this.saveChangesToOriginal();
      this.close(this._drillingHoleInfo);
    }
  }

  private setDrillingHoleInfo(hole: DrillingHoleInfo = null): void {
    if (this.isProductionCountryChanged()) {
      return;
    }
    if (hole) {
      this._drillingHoleInfo = hole;
      this._selectedDrillingHoleArticle = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
      this.toggleDrillingHoleInputComponents(true);
    } else {
      if (!this._drillingHoleInfo) {
        this._drillingHoleInfo = new DrillingHoleInfo();
      }
      this._drillingHoleInfo.position = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(this.form.controls.startOffset.value));
      if (this.selectedType) {
        this._drillingHoleInfo.shapeType = this.selectedType.toString();
      }
      if (this.selectedType) {
        this._drillingHoleInfo.size = this.selectedSize;
      }
      this._drillingHoleInfo.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();
      this._selectedDrillingHoleArticle = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
      this.toggleDrillingHoleInputComponents(true);
    }
    if (this._selectedDrillingHoleArticle) {
      this.acceptingConnections = this.getAcceptingConnectionsCount(this._selectedDrillingHoleArticle);
    }
  }

  private toggleDrillingHoleInputComponents(toggle: boolean): void {
    if (this.form.controls.endOffset.value) {
      this.endOffsetInputDisabled = !this.carrierHasPreset && toggle && Number(this.form.controls.holeCount.value) > 1 ? false : true;
    }
    if (this.form.controls.holeCount.value && !this.holeCountInputDisabled === this.carrierHasPreset) {
      this.holeCountInputDisabled = !this.carrierHasPreset;
    }
    if (this.currentMode !== EditDrillingHoleMode.DRILLING_HOLE_WIZARD_MODE) {
      if (this.selectedSize) {
        this.loadAccessories();
      } else {
        this.accessoryDropDownListEnabled = false;
      }
    }
  }

  private saveWizardDrillingHoles(): void {
    let startOffset: number = this.localeResourceManager.parseNumber(this.form.controls.startOffset.value);
    startOffset = Boolean(startOffset) ? startOffset : 0;
    VolatileDataSetHolder.instance.setDataSetValue(
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
      VolatileDataSetHolder.DATA_SET_CACHED_DRILLINGHOLE_OFFSET_KEY,
      startOffset
    );
    if (this.carrierHasPreset) {
      this.projectModel.projectChangeHandler();
      this.close(startOffset);
    } else {
      const endOffset: number = this.localeResourceManager.parseNumber(this.form.controls.endOffset.value);
      let drillingHoleCount: number = Number(this.form.controls.holeCount.value);
      const startOffsetAway3DUnits: number = UnitConverter.fromTo('mm', '3d', startOffset);
      const length: number = this.getTotalLengthMM() - startOffset - endOffset;
      let freeRangeAway3DUnits: number;
      if (this.carrierHasPreset) {
        freeRangeAway3DUnits = UnitConverter.fromTo('mm', '3d', this.presetDrillingholeDistance);
      } else {
        freeRangeAway3DUnits = drillingHoleCount === 1 ? 0 : UnitConverter.fromTo('mm', '3d', length / (drillingHoleCount - 1));
      }
      const result: DrillingHoleInfo[] = [];
      let info: DrillingHoleInfo;
      let i: number = 0;
      drillingHoleCount--;
      do {
        info = new DrillingHoleInfo();
        info.position = DrillingHoleUtils.roundDrillingHolePosition(startOffsetAway3DUnits + freeRangeAway3DUnits * i);
        info.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();
        info.shapeType = this.selectedType;
        info.size = this.selectedSize;
        result.push(info);
        i++;
      } while (i <= drillingHoleCount);
      this.projectModel.projectChangeHandler();
      this.close(result);
    }
  }

  private prepareDrillingHoleData(): void {
    if (ArrayUtils.isEmpty(this._drillingHoleArticles)) {
      this.loadAllArticles_failure();
      return;
    }

    let shapeType: string;
    let holeSize: DrillingHoleSize;
    let defaultShapeType: string = null;

    if (!this.drillingHoleInfo) {
      this.drillingHoleInfo = new DrillingHoleInfo();
    } else {
      defaultShapeType = this.drillingHoleInfo.shapeType;
    }
    this.drillingHoleInfo.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();

    if (this.carrierHasPreset && this.currentMode !== EditDrillingHoleMode.EDIT_DRILLING_HOLE_MODE) {
      const presetArticle: ArticleVO = this.getPresetArticle();
      shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(presetArticle);
      holeSize = DrillingHoleUtils.getDrillingHoleSizeByArticle(presetArticle);
      this.drillingHoleInfo.position = 0;
      this.drillingHoleInfo.shapeType = shapeType;
      this.drillingHoleInfo.size = holeSize;
      this.drillingHoleInfo.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();
      this.handleShapeType(shapeType, holeSize, true);
    } else {
      let isFirstArticle: boolean = true;
      for (const article of this._drillingHoleArticles) {
        const articleProductionCountries: any[] = this.drillingHoleCountryManager.getProductionCountriesForArticle(
          article,
          this._userDefaultProductionCountry
        );
        if (articleProductionCountries.indexOf(this._selectedProductionCountry) >= 0) {
          shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(article);
          holeSize = DrillingHoleUtils.getDrillingHoleSizeByArticle(article);
          this.handleShapeType(shapeType, holeSize, !defaultShapeType ? isFirstArticle : defaultShapeType === shapeType);
          isFirstArticle = false;
        }
      }
    }

    this.sortHoleSizes();
    this.fillDrillingInfoData(this.drillingHoleInfo);
  }

  private isProductionCountryChanged(): boolean {
    return this._selectedProductionCountry !== this.currentSelectedCountry;
  }

  private handleShapeType(shapeType: string, holeSize: DrillingHoleSize, defaultSelection: boolean = false): void {
    if (this.shapeTypeMap.containsKey(shapeType)) {
      const sizesByType: DrillingHoleSize[] = this.shapeTypeMap.getValue(shapeType);
      if (DrillingHoleUtils.findDrillingHoleSize(holeSize, sizesByType) === -1) {
        sizesByType.push(holeSize);
      }
    } else {
      this.shapeTypeMap.setValue(shapeType, [holeSize]);
      this.shapeTypes.push(shapeType);
      if (defaultSelection) {
        this._selectedType = shapeType;
      }
    }
  }

  private sortHoleSizes(): void {
    this.shapeTypeMap.values().forEach((sizes: DrillingHoleSize[]) => {
      sizes.sort((a: DrillingHoleSize, b: DrillingHoleSize) => {
        const primaryCompareResult: number = NumberUtils.numericCompare(a.sizeOnLine, b.sizeOnLine);
        if (primaryCompareResult !== 0) {
          return primaryCompareResult;
        } else {
          return NumberUtils.numericCompare(a.width, b.width);
        }
      });
    });
  }

  private fillDrillingInfoData(hole: DrillingHoleInfo): void {
    if (!hole || !this.shapeTypeMap.containsKey(hole.shapeType)) {
      return;
    }
    const availableSizes: DrillingHoleSize[] = this.shapeTypeMap.getValue(hole.shapeType);
    const index: number = DrillingHoleUtils.findDrillingHoleSize(hole.size, availableSizes);
    this._selectedSize = availableSizes[index] || null;
    this._selectedType = hole.shapeType;
    this.setDrillingHoleInfo(hole);
    if (this.carrierHasPreset && this.currentMode !== EditDrillingHoleMode.EDIT_DRILLING_HOLE_MODE) {
      this.form.controls.startOffset.setValue(this.localeResourceManager.formatNumber(this.presetStartOffset, false));
      this.updateDrillingHoleCount();
    } else {
      // convert position value to millimeters
      const position: number = UnitConverter.fromToRounded('3d', 'mm', hole.position);
      this.form.controls.startOffset.setValue(this.localeResourceManager.formatNumber(Boolean(position) ? position : 0, false));
    }
  }

  private getPresetArticle(): ArticleVO {
    const preset: DrillingholePresetVO = this.getPreset();
    return preset.drillingholeArticle;
  }

  private getPreset(): DrillingholePresetVO {
    return VolatileDataSetHolder.instance.getDataSetValue(
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEPRESET_KEY
    );
  }

  public shapeRadioButton_changeHandler(): void {
    this.selectedSize = null;
    this._selectedAccessories = null;
    this.accessories.length = 0;
  }

  public getSizeDropDownLabel(item: DrillingHoleSize): string {
    return DrillingHoleUtils.getLocalizedSizeLabel(item, this.localeResourceManager);
  }

  public getAccessoryDropDownLabel(item: ArticleVO): string {
    if (!item) {
      return '';
    }
    if (item) {
      const displayName: string = LocaleUtils.getValueByLocaleChain(item.displayName, this.localeResourceManager.getLocaleChain());
      return item.articleNo + '  ' + displayName;
    }
    return item + '';
  }

  private loadAllArticles_failure(): void {
    this.close();
    this.alertHelper.showError('editDrillingHolePopup.title.load_drilling_holes_failed', 'editDrillingHolePopup.message.load_drilling_holes_failed');
  }

  public startOffsetInputEventHandler(value: string): void {
    this.form.controls.startOffset.setValue(value);
    this.startOffsetInputHandler();
  }

  private startOffsetInputHandler(): void {
    let startOffset: number = this.localeResourceManager.parseNumber(this.form.controls.startOffset.value);
    if (NumberUtils.isNotNumber(startOffset)) {
      return;
    }

    if (this.carrierHasPreset && NumberUtils.isNumber(this.presetDrillingholeDistance)) {
      const startDrillingHoleRange: DrillingHoleRange = this.getStartDrillingHoleRange();
      if (!startDrillingHoleRange) {
        return;
      }
      this.updateDrillingHoleCount();
    }
    if (!this.carrierHasPreset && this._drillingHoleArticles) {
      const drillingHoleArticle: ArticleVO = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
      const drillingHoleSizePosOnRailOffset: number = drillingHoleArticle ? DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(drillingHoleArticle) : 0;

      if (NumberUtils.isNotNumber(drillingHoleSizePosOnRailOffset)) {
        return;
      }

      if (startOffset < -drillingHoleSizePosOnRailOffset) {
        this.form.controls.startOffset.setValue(this.localeResourceManager.formatNumber(-drillingHoleSizePosOnRailOffset, false));
        startOffset = -drillingHoleSizePosOnRailOffset;
      }
      if (startOffset > this.getTotalLengthMM() + drillingHoleSizePosOnRailOffset) {
        this.form.controls.startOffset.setValue(
          this.localeResourceManager.formatNumber(this.getTotalLengthMM() + drillingHoleSizePosOnRailOffset, false)
        );
      }
    }
  }

  private getStartDrillingHoleRange(): DrillingHoleRange {
    return DrillingHoleUtils.getStartDrillingHolePosition(this.getPreset());
  }

  private updateDrillingHoleCount(): void {
    if (this.carrierHasPreset && NumberUtils.isNumber(this.presetDrillingholeDistance)) {
      const length: number = this.getTotalLengthMM();
      const maxDrillingHolePositionOffsetOnRail: number = DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(this._selectedDrillingHoleArticle);
      this.form.controls.holeCount.setValue(
        Math.floor(
          (length + maxDrillingHolePositionOffsetOnRail - this.localeResourceManager.parseNumber(this.form.controls.startOffset.value)) /
            this.presetDrillingholeDistance +
            1
        ).toString()
      );
    }
  }

  private sizeDropDownListValueCommitHandler(): void {
    if (!this.carrierHasPreset) {
      this.setDrillingHoleInfo();
    }
  }

  private shapeTypeGroupValueCommitHandler(): void {
    if (!this.carrierHasPreset) {
      this.setDrillingHoleInfo();
    }
  }

  public endOffsetInputEventHandler(value: string): void {
    this.form.controls.endOffset.setValue(value);
    this.endOffsetInputChangeHandler();
  }

  private endOffsetInputChangeHandler(): void {
    const endOffset: number = this.localeResourceManager.parseNumber(this.form.controls.endOffset.value);
    if (NumberUtils.isNotNumber(endOffset)) {
      return;
    }

    if (!this.carrierHasPreset && this._drillingHoleArticles) {
      const drillingHoleArticle: ArticleVO = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
      const drillingHoleSizePosOnRailOffset: number = DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(drillingHoleArticle);

      if (NumberUtils.isNotNumber(drillingHoleSizePosOnRailOffset)) {
        return;
      }

      if (endOffset < -drillingHoleSizePosOnRailOffset) {
        this.form.controls.endOffset.setValue(this.localeResourceManager.formatNumber(-drillingHoleSizePosOnRailOffset, false));
      }
    }
  }

  private getRailTotalLengthLabel(): string {
    return this.localeResourceManager.getValueByKey('editDrillingHolePopup.label.total_length', {
      0: RangeOnLineConnectionUtils.getOriginalTotalLengthInMM(this.totalLength),
    });
  }

  private loadAccessories(): void {
    this.accessories.length = 0;
    this.accessoryLists.forEach((i) => (i.selectedItem = null));
    const drillingHoleArticle: ArticleVO = DrillingHoleUtils.findArticleByDrillingHole(this._drillingHoleInfo, this._drillingHoleArticles);
    this.articleIds = [];
    if (drillingHoleArticle) {
      this.articleAccessoryManager.loadAccessoryInfoIfNotExists(drillingHoleArticle.identity).subscribe(() => {
        this.articleIds = this.articleAccessoryManager.getAccessoryArticleIdsByBaseArticleIds([drillingHoleArticle.identity]);
        this.fillAccessoriesDropdown();
      });
    } else {
      this.fillAccessoriesDropdown();
    }
  }

  private fillAccessoriesDropdown() {
    this.accessories.length = 0;
    this.accessoryLists.forEach((i) => (i.selectedItem = null));
    this.articleManager.getArticles(this.articleIds).subscribe((articles: ArticleVO[]) => {
      const aArticles: ArticleVO[] = articles.sort((a: ArticleVO, b: ArticleVO) => StringUtils.stringCompare(a.articleNo, b.articleNo));
      let testIndex: number = 0;
      while (testIndex < aArticles.length) {
        const aArticle: ArticleVO = aArticles[testIndex];
        if (aArticle && this.accessories.findIndex((a) => a.identity === aArticle.identity) === -1) {
          this.accessories.push(aArticle);
        }
        testIndex++;
      }
      this.accessoriesLoadedSuccessHandler();
    });
  }

  private accessoriesLoadedSuccessHandler(): void {
    let foundPreselect: number = 0;
    this.accessoryDropDownListEnabled = true;
    if (this.accessories.length > 0) {
      if (this._selectedAccessories) {
        let i: number = 0;
        while (i < this.accessories.length) {
          const a: ArticleVO = this.accessories[i];
          // we assume each accessory article is added only once
          if (a && this._preselectedAccessoryIds.indexOf(a.identity) > -1) {
            this.setAccessoryToDropDownList(foundPreselect, a);
            foundPreselect++;
          }
          i++;
        }
      }
    }
    this.cdRef.markForCheck();
  }

  private setAccessories(drillingHoleInfo: DrillingHoleInfo): void {
    const usageId: string = drillingHoleInfo.usageId;
    this._preselectedAccessoryIds = [];
    if (!StringUtils.isEmpty(usageId)) {
      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(usageId);
      const children: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
      for (const childId of children) {
        const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(childId);
        if (childUsage) {
          const article: ArticleVO = this.articleManager.getCachedArticle(childUsage.articleId);
          if (article) {
            if (!this._selectedAccessories) {
              this._selectedAccessories = [];
            }
            this._selectedAccessories.push(article);
            this._preselectedAccessoryIds.push(article.identity);
          }
        }
      }
    }
  }

  private sizeDropDownListChangeHandler(): void {
    this._selectedAccessories = null;
  }

  public accessoryDropDownListchangeHandler(): void {
    let i: number = 0;
    this._selectedAccessories = [];
    while (i <= this.acceptingConnections) {
      const article: ArticleVO = this.getAccessoryFromDropDownList(i);
      if (article && this._selectedAccessories.indexOf(article) < 0) {
        this._selectedAccessories.push(article);
      }
      i++;
    }
  }

  // could be enhanced to allow > 3 accessories
  private getAccessoryFromDropDownList(index: number): ArticleVO {
    return this.accessoryLists[index].selectedItem || null;
  }

  private setAccessoryToDropDownList(index: number, article: ArticleVO): void {
    if (ArrayUtils.isNotEmpty(this.accessoryLists) && this.accessoryLists[index]) {
      this.accessoryLists[index].selectedItem = article;
    }
  }

  private createAccessoryLists(): void {
    this.accessoryLists = [];
    for (let i = 0; i <= this.acceptingConnections; i++) {
      this.accessoryLists.push({
        items: this.accessories,
        selectedItem: null,
      });
    }
  }

  private getAcceptingConnectionsCount(article: ArticleVO): number {
    let result: number = -1;
    if (article && ArrayUtils.isNotEmpty(article.metaElements)) {
      for (const metaElement of article.metaElements) {
        if (metaElement instanceof ConnectionAreaVO && metaElement.connectionSide === ConnectionAreaVO.SIDE_ACCEPTING) {
          result++;
        }
      }
    }
    // currently we can only edit one accessory.
    return Math.min(result, 0);
  }

  // save changes in original DrillingHoleInfo
  private saveChangesToOriginal(): void {
    if (this._originalDrillingHoleInfo) {
      this._originalDrillingHoleInfo.dirty = this.drillingHoleInfo.dirty;
      this._originalDrillingHoleInfo.position = this.drillingHoleInfo.position;
      this._originalDrillingHoleInfo.positionOffset = this.drillingHoleInfo.positionOffset;
      this._originalDrillingHoleInfo.shapeType = this.drillingHoleInfo.shapeType;
      this._originalDrillingHoleInfo.size = this.drillingHoleInfo.size;
      this._originalDrillingHoleInfo.drillDimmensionID = this.drillingHoleInfo.drillDimmensionID;
      this._originalDrillingHoleInfo.accessoryArticles = this.drillingHoleInfo.accessoryArticles;
    }
  }
}
